import { Component, Injector, OnDestroy, OnInit } from "@angular/core";
import { ProgressCategory, ProgressCategoryBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressProduct, ProgressProductBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ProgressService } from "@common/progress/progress.service";
import { PageRouteBuilder } from "@common/route/page-route-builder";
import { PreventNavigationGuard } from "@common/route/prevent-navigation.guard";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { IDxCustomRuleValidationCallbackEvent } from "@common/ux/dx.types";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleEntity, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { AddProgressToOrganisationDialogComponent } from "app/features/progress/add-progress-to-organisation-dialog/add-progress-to-organisation-dialog.component";
import { ReorderEvent } from "devextreme/ui/sortable";
import { defer, lastValueFrom, of, tap } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Component({
    selector: "adapt-progress-list-page",
    standalone: false,
    templateUrl: "./progress-list-page.component.html",
})
export class ProgressListPageComponent extends BaseRoutedComponent implements OnInit, OnDestroy {
    public progressProducts: ProgressProduct[] = [];

    private removePreventNavigationCallback?: () => void;

    public constructor(
        injector: Injector,
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        private progressService: ProgressService,
        private preventNavigationGuard: PreventNavigationGuard,
    ) {
        super(injector);

        this.removePreventNavigationCallback = this.preventNavigationGuard.registerCanNavigateCallback(() => defer(() => {
            // disable navigation when there are unsaved changes
            if (!this.commonDataService.hasChanges()) {
                return of(true);
            }

            // otherwise confirm to discard changes
            return this.dialogService.openConfirmDiscardDialog().pipe(
                map((result) => !!result),
            );
        }));
    }

    public async ngOnInit() {
        // disable save overlay for progress items
        this.commonDataService.disableSaveOverlayForEntityTypes(ProgressItem);

        await this.updateData();

        this.notifyActivated();
    }

    public get hasChanges() {
        return this.commonDataService.hasChanges();
    }

    public ngOnDestroy() {
        // re-enable save overlay for progress items
        this.commonDataService.enableSaveOverlayForEntityTypes(ProgressItem);

        // allow navigation again
        this.removePreventNavigationCallback?.();
    }

    public onProductReorder(e: ReorderEvent) {
        // only reorder if actually changed
        if (e.fromIndex !== e.toIndex) {
            SortUtilities.reorderItemInIntegerSortedArray(this.progressProducts, "ordinal", e.fromIndex!, e.toIndex!);
        }

        this.commonDataService.saveEntities(this.progressProducts).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public onCategoryReorder(e: ReorderEvent, product: ProgressProduct) {
        // only reorder if actually changed
        if (e.fromIndex !== e.toIndex) {
            SortUtilities.reorderItemInIntegerSortedArray(product.progressCategories, "ordinal", e.fromIndex!, e.toIndex!);
        }

        this.commonDataService.saveEntities(product.progressCategories).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    @Autobind
    public createNewProgressProduct() {
        return this.commonDataService.create(ProgressProductBreezeModel, {
            ordinal: this.getLastProductOrdinal() + 1,
        }).pipe(
            switchMap((progressProduct) => this.editProgressProduct(progressProduct)),
        );
    }

    @Autobind
    public editProgressProduct(progressProduct: ProgressProduct) {
        const entities: IEditSimpleEntity[] = [{
            entity: progressProduct,
            fieldName: "name",
            label: "Name",
            type: SimpleValueType.Text,
            usePropertyValidator: true,
        }];

        // only when editing in nimbus since system products need a code
        if (!progressProduct.organisationId) {
            entities.push({
                entity: progressProduct,
                fieldName: "code",
                label: "Code",
                type: SimpleValueType.Text,
                usePropertyValidator: true,
                customValidators: [{
                    validator: ({ value }: IDxCustomRuleValidationCallbackEvent) => typeof value === "string"
                        ? !this.progressProducts.some((i) => i.code?.trim().toLowerCase() === value.trim().toLowerCase() && i !== progressProduct)
                        : false,
                    message: "A product with that code already exists",
                }, {
                    validator: ({ value }: IDxCustomRuleValidationCallbackEvent) => typeof value === "string"
                        ? !!value.trim()
                        : false,
                    message: "Enter a code for this product",
                }],
            });
        }

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: (progressProduct.entityAspect.entityState.isAdded() ? "Add" : "Edit") + " progress product",
            entities,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public createNewProgressCategory(progressProduct: ProgressProduct) {
        return this.commonDataService.create(ProgressCategoryBreezeModel, {
            progressProduct,
            ordinal: this.getLastCategoryOrdinal(progressProduct) + 1,
        }).pipe(
            switchMap((progressCategory) => this.editProgressCategory(progressCategory)),
        );
    }

    @Autobind
    public addProductsToOrganisation() {
        return this.dialogService.open(AddProgressToOrganisationDialogComponent);
    }

    @Autobind
    public deleteProduct(progressProduct: ProgressProduct) {
        return this.dialogService.openConfirmationDialog({
            title: "Delete progress product",
            message: `Are you sure you want to delete the progress product "${progressProduct.name}"?`,
            checkboxMessage: progressProduct.code
                ? "This is a system product, please confirm you intend to delete this."
                : undefined,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => this.commonDataService.remove(progressProduct)),
            tap(() => {
                ArrayUtilities.removeElementFromArray(progressProduct, this.progressProducts);
                SortUtilities.updateIntegerSortedArrayAfterItemRemoval(this.progressProducts, "ordinal", progressProduct.ordinal);
            }),
            switchMap(() => this.commonDataService.saveEntities([...this.progressProducts, progressProduct])),
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public deleteCategory(category: ProgressCategory) {
        const progressCategories = category.progressProduct.progressCategories;
        return this.dialogService.openConfirmationDialog({
            title: "Delete progress category",
            message: `Are you sure you want to delete the progress category "${category.name}"?`,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => this.commonDataService.remove(category)),
            tap(() => {
                ArrayUtilities.removeElementFromArray(category, progressCategories);
                SortUtilities.updateIntegerSortedArrayAfterItemRemoval(progressCategories, "ordinal", category.ordinal);
            }),
            switchMap(() => this.commonDataService.saveEntities([...progressCategories, category])),
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public editProgressCategory(progressCategory: ProgressCategory) {
        const entities: IEditSimpleEntity[] = [{
            entity: progressCategory,
            fieldName: "name",
            label: "Name",
            type: SimpleValueType.Text,
            usePropertyValidator: true,
        }];

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: (progressCategory.entityAspect.entityState.isAdded() ? "Add" : "Edit") + ` progress category within product "${progressCategory?.progressProduct.name}"`,
            entities,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData).pipe(
            switchMap(() => this.updateData()),
        );
    }

    private async updateData() {
        this.progressProducts = await lastValueFrom(this.progressService.getTemplateProgressProducts());
        for (const progressProduct of this.progressProducts) {
            progressProduct.progressCategories.sort(SortUtilities.getSortByFieldFunction<ProgressCategory>("ordinal"));
        }
    }

    private getLastProductOrdinal() {
        return this.progressProducts.reduce((max, product) => Math.max(max, product.ordinal), -1);
    }

    private getLastCategoryOrdinal(progressProduct: ProgressProduct) {
        return progressProduct.progressCategories.reduce((max, category) => Math.max(max, category.ordinal), -1);
    }
}

export const progressListPageRoute = new PageRouteBuilder()
    .usingNgComponent("adapt-progress-list-page", ProgressListPageComponent)
    .atUrl("/tools/progress-products")
    .withTitle("Progress Products")
    .build();
